// src/lib/formatTime.ts
// Extracted out of MarketAlertsPage once Bot Health needed the exact
// same relative-time formatting — avoids a third copy-paste when the
// next page needs it too.

/** Time since a past event, e.g. "2h ago". "Never" if null. */
export function formatRelative(iso: string | null) {
  if (!iso) return 'Never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Time until a future event, e.g. "in 2h". "Never" if null, "Expired" if already past. */
export function formatExpiry(iso: string | null) {
  if (!iso) return 'Never';
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return 'Expired';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

/**
 * Relative time in whichever direction the timestamp actually falls -
 * "3d ago" if past, "in 1d" if future. Needed anywhere the same field
 * could be either depending on context (e.g. a clan event's end time:
 * already passed for an ended event, still upcoming for an active
 * one) - formatRelative and formatExpiry above are each deliberately
 * one-directional and give a wrong answer if used for the other case
 * (found 2026-07-16: formatRelative on a future timestamp produces a
 * negative diff, and `minutes < 1` catches every negative number,
 * so any future time at all - a day out or a year out - silently
 * showed "Just now").
 */
export function formatRelativeEither(iso: string | null): string {
  if (!iso) return 'Never';
  const diffMs = new Date(iso).getTime() - Date.now();
  const isFuture = diffMs > 0;
  const absMinutes = Math.round(Math.abs(diffMs) / 60000);

  if (absMinutes < 1) return 'Just now';
  const minutes = absMinutes;
  if (minutes < 60) return isFuture ? `in ${minutes}m` : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return isFuture ? `in ${hours}h` : `${hours}h ago`;
  const days = Math.round(hours / 24);
  return isFuture ? `in ${days}d` : `${days}d ago`;
}

/** Exact local date/time, e.g. "2026-07-14, 14:00" — pairs with formatRelative for logs/tables where both the "how long ago" and the precise moment matter. */
export function formatExactDateTime(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${date}, ${time}`;
}
